import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useForm, useWatch } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  useCreateProviderMutation,
  usePartnerShortQuery,
  usePracticeShortQuery,
  useProviderClinicalShortQuery,
  useProviderRegionShortQuery,
} from '@common/cache';
import { createProviderSchema } from '../schemas';
import dayjs, { Dayjs } from 'dayjs';
import { ProviderSpecialtyType } from '@common/types';
import { formatApiError } from '@common/errors/error-formatting.ts';

interface ICreateProviderFormShape {
  first_name: string;
  last_name: string;
  suffix?: string;
  external: boolean;
  npi: string;
  guide_certified: boolean;
  guide_certification_date: Dayjs;
  partner_id: string;
  practice_id: string;
  email?: string;
  phone_number?: string;
  provider_specialty: string;
  slack_link?: string;
  region_id?: string | null;
  clinical_leader_id?: string | null;
  supervising_physician_id?: string | null;
}

const initialValues: Partial<ICreateProviderFormShape> = {
  first_name: '',
  last_name: '',
  suffix: '',
  external: false,
  npi: '',
  guide_certified: false,
  guide_certification_date: dayjs(new Date()),
  partner_id: '',
  practice_id: '',
  email: '',
  phone_number: '',
  provider_specialty: '',
  slack_link: '',
  region_id: null,
  clinical_leader_id: null,
  supervising_physician_id: null,
};

const providerSpecialtyOptions: ProviderSpecialtyType[] = [
  'Cardiology',
  'Palliative care',
  'Primary care',
  'Neurology',
  'Geropsych',
  'Home Health',
  'Social Worker',
];

export const useCreateProviderModal = (
  isOpen: boolean,
  handleClose: () => void,
  handleOpenProviderDetails: (id: string) => void,
) => {
  const formMethods = useForm<ICreateProviderFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<ICreateProviderFormShape, any, any>(
      createProviderSchema,
    ),
    defaultValues: initialValues,
  });
  const {
    handleSubmit,
    setValue,
    reset,
    getValues,
    formState: { isDirty, isValid },
  } = formMethods;

  const part_id = useWatch({
    control: formMethods.control,
    name: 'partner_id',
  });

  const { data: partnerOptions = [] } = usePartnerShortQuery();
  const { data: practiceOptions = [], refetch } = usePracticeShortQuery({
    record_id: part_id || undefined,
  });
  const { mutateAsync, isPending } = useCreateProviderMutation();
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const [regionSearchQuery, setRegionSearchQuery] = useState('');
  const [clinicalSearchQuery, setClinicalRegionSearchQuery] = useState('');

  const {
    data: providerRegionOptions = [],
    isLoading: isRegionOptionsLoading,
  } = useProviderRegionShortQuery(regionSearchQuery || undefined);

  const {
    data: providerClinicalOptions = [],
    isLoading: isClinicalOptionsLoading,
  } = useProviderClinicalShortQuery(clinicalSearchQuery || undefined);

  useEffect(() => {
    if (part_id && isOpen) {
      refetch();
    }
  }, [part_id, isOpen]);

  const onCloseAction = () => {
    if (isDirty) {
      return setIsSaveChangesModalOpen(true);
    }
    handleClose();
    reset();
  };

  const handleChangeRegionQuery = (value: string) => {
    setRegionSearchQuery(value);
  };

  const handleChangeClinicalQuery = (value: string) => {
    setClinicalRegionSearchQuery(value);
  };

  const submitHandler = async (values: ICreateProviderFormShape) => {
    try {
      const response = await mutateAsync({
        first_name: values.first_name,
        last_name: values.last_name,
        suffix: values.suffix,
        external: values.external,
        npi: values.npi,
        guide_certified: values.guide_certified,
        guide_certification_date:
          values.guide_certification_date.format('YYYY-MM-DD'),
        record_id: values.practice_id,
        email: values.email,
        phone_number: values.phone_number,
        provider_specialty: values.provider_specialty as ProviderSpecialtyType,
        slack_link: values.slack_link || null!,
        ...(values.region_id && { region_id: values.region_id }),
        ...(values.supervising_physician_id && {
          supervising_physician_id: values.supervising_physician_id,
        }),
        ...(values.clinical_leader_id && {
          clinical_leader_id: values.clinical_leader_id,
        }),
      });

      if (response.data.id) {
        handleOpenProviderDetails(response.data.id);
      }

      handleClose();
      reset();

      toast.success('You added a new provider');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    handleClose();
    reset();
  };

  const handleSaveChanges = async () => {
    const values = getValues();

    try {
      await mutateAsync({
        first_name: values.first_name,
        last_name: values.last_name,
        suffix: values.suffix,
        external: values.external,
        npi: values.npi,
        guide_certified: values.guide_certified,
        guide_certification_date:
          values.guide_certification_date.format('YYYY-MM-DD'),
        record_id: values.practice_id,
        email: values.email,
        phone_number: values.phone_number,
        provider_specialty: values.provider_specialty as ProviderSpecialtyType,
        slack_link: values.slack_link || null!,
        ...(values.region_id && { record_id: values.region_id }),
        ...(values.supervising_physician_id && {
          supervising_physician_id: values.supervising_physician_id,
        }),
        ...(values.clinical_leader_id && {
          clinical_leader_id: values.clinical_leader_id,
        }),
      });

      toast.success('You added a new provider');
      setIsSaveChangesModalOpen(false);
      handleClose();
      reset();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  return {
    providerSpecialtyOptions: providerSpecialtyOptions.map(o => ({
      value: o,
      label: o,
    })),
    practiceOptions: practiceOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    partnerOptions: partnerOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    formMethods,
    onSubmit,
    isOpen,
    submitHandler,
    handleSubmit,
    onCloseAction,
    setValue,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
    handleChangeRegionQuery,
    providerRegionOptions: providerRegionOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    providerClinicalOptions: providerClinicalOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    regionSearchQuery,
    isRegionOptionsLoading,
    clinicalSearchQuery,
    isClinicalOptionsLoading,
    handleChangeClinicalQuery,
  };
};
